import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { auth } from "components/firebase";
import { HANDLE_USER_SUBSCRIPTION } from "graphql/mutations/utils";
import { UPDATE_USER } from "graphql/mutations/user";
import { EXPENSES, SETTLEMENT } from "graphql/queries/expense";
import { PUBLIC_USER, USER } from "graphql/queries/user";
import { USER_PROFILE_IMAGE_LINK } from "graphql/queries/utils";
import { useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import useReactRouter from "use-react-router";
import { analytics } from "./analytics";
import useLoyalty from "./useLoyalty";

// useUser mounts in many places — guard the on-login subscription sync so it fires once per session.
let subscriptionSyncAttempted = false;

const useUser = () => {
  const { match }: { match: any } = useReactRouter();
  const [firebaseUser, initializing] = useAuthState(auth);

  // Queries
  const { data, client, refetch, error } = useQuery(USER, { errorPolicy: "all" });
  const [getUserProfileImageLink, { data: userProfileImageLinkData }] = useLazyQuery(USER_PROFILE_IMAGE_LINK);
  const { data: publicData } = useQuery(PUBLIC_USER, {
    variables: { username: match?.params?.username },
    skip: !match?.params?.username,
  });
  const user = data?.user || {};
  const publicUser = publicData?.publicUser || {};
  const { claimReferral } = useLoyalty();

  // Mutations
  const [updateUser] = useMutation(UPDATE_USER);
  const [syncSubscription] = useMutation(HANDLE_USER_SUBSCRIPTION);

  useEffect(() => {
    if (data?.user) {
      const referralCode = sessionStorage.getItem("referralCode");
      const campaignId = sessionStorage.getItem("campaignId");
      const fpr = sessionStorage.getItem("fpr");
      const _by = sessionStorage.getItem("_by");
      const city = sessionStorage.getItem("city");
      sessionStorage.removeItem("referralCode");
      sessionStorage.removeItem("campaignId");
      sessionStorage.removeItem("fpr");
      sessionStorage.removeItem("_by");
      sessionStorage.removeItem("city");
      if (referralCode) {
        claimReferral({ variables: { referralId: referralCode } });
      }
      if (_by || fpr) {
        //@ts-ignore
        // window.fpr("sale", { uid: user?.firebaseId, amount: 0.5, currency: "USD" });
        analytics.identify(user?.firebaseId);
        analytics.people.set({
          $stippl_campaign: "fpr",
          $fpr_promoter: _by || fpr,
        });
        claimReferral({ variables: { fpr: _by || fpr, city } });
      }
      if (campaignId) {
        claimReferral({ variables: { campaignId } });
        analytics.identify(user?.firebaseId);
        analytics.people.set({
          $stippl_campaign: campaignId,
        });
      }
    }
  }, [data?.user]);

  useEffect(() => {
    !!firebaseUser?.email && setTimeout(() => client.refetchQueries({ include: [USER] }), 200);
  }, [firebaseUser?.email]);

  // One-shot per session: heal the "bought PRO on mobile but web still shows free" case (a purchase
  // whose RC webhook was dropped on an anonymous id). Gate on the RC web SDK entitlement — only when
  // this device's user is genuinely entitled do we call the backend (which does the authoritative RC
  // check). Free users never trigger a backend RC lookup; the RC SDK chunk is loaded lazily.
  useEffect(() => {
    if (subscriptionSyncAttempted) return;
    const userId = data?.user?._id;
    if (!userId || data?.user?.hasSubscription !== false) return;
    subscriptionSyncAttempted = true;

    (async () => {
      try {
        const { initRevenueCat } = await import("lib/revenuecat");
        const entitled = await initRevenueCat(userId).isEntitledTo("Stippl Pro");
        if (!entitled) return;
        await syncSubscription();
        await client.refetchQueries({ include: [USER] });
      } catch (e: any) {
        console.error("[RC] entitlement heal check failed:", e?.message || e);
      }
    })();
  }, [data?.user?._id, data?.user?.hasSubscription]);

  const stipplUser = {
    ...user,
    displayName: user?.displayName?.replace("+", " ") || firebaseUser?.displayName?.replace("+", " "),
    avatar: firebaseUser?.providerData?.map((data) => data.photoURL)?.[0],
  };

  return {
    user: stipplUser,
    // The profile query's error (also set while nobody is signed in: `user` requires a login). Only /connect reads it.
    error,
    initializing,
    publicUser,
    userProfileImageLinkData,
    updateUser: (input, onCompleted: any = () => "") =>
      updateUser({ variables: { input }, refetchQueries: input.currency ? [EXPENSES, SETTLEMENT] : [], onCompleted }),
    refetch,
    getUserProfileImageLink,
  };
};

export default useUser;
